import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {ChangeDetectionStrategy, Component, inject} from '@angular/core';
import {comparator, TranslatePipe, TranslateService} from '@brunata-angular/util';
import {ApiTokenService} from './api-token.service';
import {CommonModule} from '@angular/common';
import {CreateApiTokenModalComponent} from './create-api-token-modal/create-api-token-modal.component';
import {ApiToken} from './api-token.model';
import {filter, map, startWith, Subject, switchMap} from 'rxjs';
import {ConfirmationDialogService, DatePipe} from '@brunata-angular/ui';
import {ApiTokenCreatedModalComponent} from './api-token-created-modal/api-token-created-modal.component';
import {take} from 'rxjs/operators';
import {Store} from '@ngrx/store';
import {selectUser} from 'src/app/state/selectors';
import {UserStateSuccess} from 'src/app/state/reducers';

@Component({
    selector: 'app-api-token-table',
    standalone: true,
    imports: [TranslatePipe, DatePipe, CommonModule],
    templateUrl: './api-token-table.component.html',
    styleUrl: './api-token-table.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ApiTokenTableComponent {
    private readonly store = inject(Store);
    private readonly apiTokenSubject = new Subject<void>();
    readonly user$ = this.store.select(selectUser).pipe(
        filter((userState): userState is UserStateSuccess => userState.status === 'success'),
        map(userState => userState.user),
    );
    readonly apiTokens$ = this.apiTokenSubject.pipe(
        startWith(null),
        switchMap(() =>
            this.apiTokenService
                .getApiTokens()
                .pipe(
                    map(tokens =>
                        tokens.sort(
                            comparator({selector: meter => meter.createDate, order: 'desc'}),
                        ),
                    ),
                ),
        ),
    );

    constructor(
        private apiTokenService: ApiTokenService,
        private modalService: NgbModal,
        private confirmationDialogService: ConfirmationDialogService,
        private translateService: TranslateService,
    ) {}

    revokeToken(token: ApiToken): void {
        this.confirmationDialogService
            .confirm(
                this.translateService.translate('common_warning', 'online', 'common'),
                this.translateService.translate('utilities_revoke_api_token_warning'),
            )
            .then(confirmed => {
                if (!confirmed) {
                    return;
                }
                this.apiTokenService.revokeToken(token).subscribe(() => {
                    this.apiTokenSubject.next();
                });
            })
            .catch(() => false);
    }

    createApiToken(): void {
        this.apiTokens$.pipe(take(1)).subscribe(tokens => {
            const modelRefCreateApiTokenModalComponent = this.modalService.open(
                CreateApiTokenModalComponent,
                {
                    size: 'md',
                    backdrop: 'static',
                },
            );

            const instanceCreateApiTokenModalComponent =
                modelRefCreateApiTokenModalComponent.componentInstance as CreateApiTokenModalComponent;

            // Pass existing tokens to modal to check for name uniqueness
            instanceCreateApiTokenModalComponent.apiTokens = tokens;

            instanceCreateApiTokenModalComponent.apiToken.subscribe(token => {
                this.apiTokenSubject.next();
                const modalRefApiTokenCreatedModalComponent = this.modalService.open(
                    ApiTokenCreatedModalComponent,
                    {
                        size: 'md',
                        backdrop: 'static',
                    },
                );
                const instanceApiTokenCreatedModalComponent =
                    modalRefApiTokenCreatedModalComponent.componentInstance as ApiTokenCreatedModalComponent;
                instanceApiTokenCreatedModalComponent.token = token.token;
            });
        });
    }
}
